import { useReducer, Reducer, useCallback, useEffect } from 'react';

type PagingActionTypes = 'FETCH' | 'RESOLVE' | 'REJECT' | 'CANCEL' | 'REFETCH';

interface PagingAction<P> {
  type: PagingActionTypes;
  data?: P;
  error?: string;
}

type Status = 'idle' | 'success' | 'loading' | 'failure';

interface PagingState<P> {
  pageIndex: number;
  data: P[];
  prevStatus: Status;
  status: Status;
  error?: string;
}

function pagingReducer<P>(
  state: PagingState<P>,
  event: PagingAction<P>,
): PagingState<P> {
  const withPrevStatus = (newState: PagingState<P>): PagingState<P> => ({
    ...newState,
    prevStatus: state.status,
  });
  switch (event.type) {
    case 'FETCH':
      return withPrevStatus({
        ...state,
        status: 'loading',
      });
    case 'RESOLVE':
      return withPrevStatus({
        ...state,
        status: 'success',
        pageIndex: state.pageIndex + 1,
        data: state.data.concat(event.data ?? []),
      });
    case 'REJECT':
      return withPrevStatus({
        ...state,
        status: 'failure',
        error: event.error,
      });
    case 'CANCEL':
      return withPrevStatus({
        ...state,
        status: 'idle',
      });
    case 'REFETCH':
      return initialState;
    default:
      return state;
  }
}

const initialState: PagingState<any> = {
  pageIndex: -1,
  prevStatus: 'idle',
  status: 'idle',
  data: [],
};

export default function usePaging<P, A = unknown>(
  getArgs: (currIndex: number, currData?: P | null) => A | null,
  fetcher: (args: A) => Promise<P>,
  initialData: P | null = null,
) {
  const [state, dispatch] = useReducer<
    Reducer<PagingState<P>, PagingAction<P>>
  >(pagingReducer, {
    ...initialState,
    data: initialData ? [initialData] : [],
    pageIndex: initialData ? 0 : initialState.pageIndex,
  });

  const fetchMore = useCallback(() => {
    dispatch({ type: 'FETCH' });
  }, []);

  const refetch = useCallback(() => {
    dispatch({ type: 'REFETCH' });
  }, []);

  useEffect(() => {
    const doFetch = async () => {
      const args = getArgs(state.pageIndex, state.data[state.pageIndex]);
      if (args === null) {
        dispatch({ type: 'CANCEL' });
        return;
      }
      try {
        const res = await fetcher(args);
        dispatch({ type: 'RESOLVE', data: res });
      } catch (e) {
        dispatch({ type: 'REJECT', error: e });
      }
    };

    // Only fetch more if the previous status was not 'loading'. This make sure
    // requests cannot be made while there is still an ongoing one.
    if (state.status === 'loading' && state.prevStatus !== 'loading') {
      doFetch();
    }
    // useEffect only depends on the change of the state and nothing else
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  return {
    fetchMore,
    refetch,
    isLoading: state.status === 'loading',
    ...state,
  };
}
